<template>
  <button
    type="button"
    class="inline-flex shrink-0 items-center justify-center transition-all"
    :class="[
      sizeClasses,
      shapeClasses,
      variantClasses,
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    ]"
    :disabled="disabled"
    :aria-label="ariaLabel"
    :data-size="actualSize"
    :data-variant="actualVariant"
    :data-shape="actualShape"
    @click="emit('click', $event)"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

type Size = 'sm' | 'base' | 'md' | 'lg';
type Variant =
  | 'default'
  | 'outline'
  | 'ghost'
  | 'subtle'
  | 'outline-primary'
  | 'outline-subtle';
type Shape = 'circle' | 'rounded';

const props = defineProps<{
  size?: Size;
  variant?: Variant;
  shape?: Shape;
  disabled?: boolean;
  ariaLabel: string;
}>();

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const actualSize = computed(() => props.size ?? 'md');
const actualVariant = computed(() => props.variant ?? 'default');
const actualShape = computed(() => props.shape ?? 'circle');

const sizeClasses = computed(() => {
  const map: Record<Size, string> = {
    sm: 'w-7 h-7',
    base: 'w-8 h-8',
    md: 'w-9 h-9',
    lg: 'w-11 h-11',
  };
  return map[actualSize.value];
});

const shapeClasses = computed(() => {
  const map: Record<Shape, string> = {
    circle: 'rounded-full',
    rounded: 'rounded-lg',
  };
  return map[actualShape.value];
});

const variantClasses = computed(() => {
  const map: Record<Variant, string> = {
    default:
      'bg-white border border-solid border-fig-gray-300 text-slate-600 hover:bg-fig-gray-100',
    outline:
      'bg-transparent border border-solid border-accent text-accent hover:bg-primary-100',
    ghost: 'bg-transparent text-fig-gray-600 hover:bg-fig-gray-100',
    subtle: 'bg-btn-subtle text-slate-600 hover:bg-btn-subtle-hover',
    'outline-primary':
      'bg-white border border-solid border-primary text-primary hover:bg-btn-secondary-hover',
    'outline-subtle':
      'bg-white border border-solid border-slate-300 text-primary hover:bg-btn-secondary-hover',
  };
  return map[actualVariant.value];
});
</script>
